import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { Smile, Anchor, ShieldCheck, Activity, Layers, ScanEye, Microscope, CheckCircle2 } from "lucide-react";
import image1 from '../../Assets/Services/Service2-2.jpg'
import image2 from '../../Assets/Services/Service2-3.jpg'
import { Helmet } from "react-helmet";


const DentalImplants = () => {
  return (
    <ServiceLayout
      title="Advanced Dental Implants"
      subtitle="The gold standard for tooth replacement. “Smile, speak, and eat with confidence again.”"
      heroImage="/images/hero/implants.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <Helmet>
  <title>Dental Implants Vijayawada | Tooth Replace</title>
  <meta name="description" content="Best dental implants in Vijayawada for missing teeth with safe, painless and long-lasting results by experts." />
  <meta name="keywords" content="dental implants Vijayawada, tooth replacement"/>
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical"href="https://sridhardental.com/service/dental-implants" />
</Helmet>

        <p className="text-lg text-gray-700 leading-relaxed">
          Missing teeth can affect not only your smile but also your ability to eat comfortably and maintain
good oral health. Dental implants are one of the most advanced and reliable solutions for
replacing missing teeth and restoring your natural smile.
        </p>
        <p className="mt-4 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, we offer advanced dental implant treatments in
Vijayawada and Hyderabad using modern technology, state-of-the-art facilities, and experienced
implant specialists to help you smile with confidence again.
        </p>
      </section>

      {/* What are Implants Box */}
      <section className="bg-blue-900 text-white p-8 rounded-2xl mb-12 shadow-lg relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 !text-blue-300">
            <Anchor className="text-blue-300" /> What are Dental Implants?
          </h2>
          <p className="!text-blue-50 leading-relaxed">
            Dental implants are artificial tooth roots made of biocompatible titanium that are placed into the jawbone. Once the implant integrates with the bone, it provides a strong and stable foundation for permanent replacement teeth like crowns or bridges, closely mimicking natural teeth.
          </p>
        </div>
        <Anchor className="absolute -bottom-10 -right-10 w-64 h-64 text-white/5 rotate-12" />
      </section>

      {/* Common Uses Grid */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Common Applications:</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Replacement of a single missing tooth",
            "Replacement of multiple missing teeth",
            "Full mouth rehabilitation",
            "Implant-supported bridges and dentures",
          ].map((use, idx) => (
            <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <CheckCircle2 className="text-blue-600 w-5 h-5 flex-shrink-0" />
              <span className="font-semibold text-gray-700">{use}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Implants - Icon Grid */}
      <section className="mb-12 border-y py-12">
        <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Why Choose Dental Implants?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: <Smile className="text-blue-600" />, title: "Natural Look & Feel", desc: "Closely mimics your natural teeth." },
            { icon: <Activity className="text-blue-600" />, title: "Improved Chewing", desc: "Eat your favorite foods with full strength." },
            { icon: <Layers className="text-blue-600" />, title: "Bone Preservation", desc: "Helps maintain jawbone structure and facial shape." },
            { icon: <ShieldCheck className="text-blue-600" />, title: "Long-Lasting", desc: "A durable solution designed to last many years." },
            { icon: <CheckCircle2 className="text-blue-600" />, title: "Tooth Safety", desc: "No damage to adjacent healthy natural teeth." },
            { icon: <Activity className="text-blue-600" />, title: "Clear Speech", desc: "Speak with confidence without worrying about slips." },
          ].map((benefit, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="bg-blue-50 p-3 rounded-lg h-fit">{benefit.icon}</div>
              <div>
                <h4 className="font-bold text-gray-900">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Advanced Facilities Section */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-blue-900 mb-8">Advanced Implant Facilities</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-blue-100 p-6 rounded-xl shadow-sm hover:shadow-md transition">
            <Microscope className="text-blue-600 w-10 h-10 mb-4" />
            <h3 className="text-xl font-bold mb-2">Sophisticated Operation Theatre</h3>
            <p className="text-gray-600 text-sm">
              Our surgeries are performed in fully equipped theatres designed to maintain the highest standards of sterility and safety.
            </p>
          </div>
          <div className="bg-white border border-blue-100 p-6 rounded-xl shadow-sm hover:shadow-md transition">
            <ScanEye className="text-blue-600 w-10 h-10 mb-4" />
            <h3 className="text-xl font-bold mb-2">3D CBCT Diagnostics</h3>
            <p className="text-gray-600 text-sm">
              We use 3D evaluation for precise implant placement planning, ensuring highly predictable and safe results.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gray-50 p-10 rounded-3xl mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">The Treatment Journey</h2>
        <div className="space-y-6">
          {[
            "Detailed Consultation & Smile Evaluation",
            "Advanced Digital Imaging",
            "Precise Implant Placement",
            "Healing & Bone Integration",
            "Final Crown Placement",
          ].map((step, idx) => (
            <div key={idx} className="flex items-center gap-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                {idx + 1}
              </div>
              <p className="text-gray-700 font-medium">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Conclusion */}
      <section className="text-center max-w-2xl mx-auto">
        <p className="text-gray-600 italic">
          “Regain your confident smile and enjoy eating, speaking, and living comfortably again with
safe, precise, and long-lasting dental implant treatment.”
        </p>
      </section>
    </ServiceLayout>
  );
};

export default DentalImplants;